import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/authService';
import { tenantService } from '@/services/tenantService';

export interface PresenceUser {
  id: string;
  name: string;
  initials: string;
  color: string;
  lastActive: string;
}

export function usePresence(channelName: string = 'global_presence') {
  const [onlineUsers, setOnlineUsers] = useState<PresenceUser[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    
    tenantService.getCurrentTenant().then(t => {
      if (mounted && t) {
        setTenantId(t.id);
      }
    });

    return () => { mounted = false; };
  }, []);

  const actualChannelName = tenantId ? `tenant:${tenantId}:presence` : channelName;

  useEffect(() => {
    let mounted = true;
    if (!user) return;
    
    // Only connect once we have the tenant ID if we expect one
    // But allow connection for admins who skip this anyway
    const isAdmin = authService.isAdmin();
    if (isAdmin) {
      console.log('[Presence] Admin user detected - skipping presence connection');
      return;
    }

    // Wait until we have a tenant ID to avoid connecting to "global_presence" first
    // unless explicitly requested
    if (channelName === 'global_presence' && !tenantId) {
      return;
    }

    console.log(`[Presence] Connecting to channel: ${actualChannelName}`);

    const channel = supabase.channel(actualChannelName, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    const name = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
    const initials = name.split(' ').map((n: any) => n[0]).join('').toUpperCase().slice(0, 2);
    const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-amber-500', 'bg-rose-500', 'bg-indigo-500', 'bg-violet-500'];
    const color = colors[Math.abs(user.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % colors.length];

    channel
      .on('presence', { event: 'sync' }, () => {
        if (!mounted) return;
        const state = channel.presenceState();
        const users: PresenceUser[] = [];
        for (const key in state) {
          const presence = state[key][0] as any;
          users.push({
            id: key,
            name: presence.name,
            initials: presence.initials,
            color: presence.color,
            lastActive: new Date().toISOString(),
          });
        }
        setOnlineUsers(users);
      })
      .subscribe(async (status, err) => {
        if (!mounted) return;
        if (status === 'SUBSCRIBED') {
          console.log(`[Presence] Successfully joined ${actualChannelName}`);
          setIsConnected(true);
          await channel.track({
            name,
            initials,
            color,
            online_at: new Date().toISOString(),
          });
        } else if (status === 'CLOSED') {
          setIsConnected(false);
        } else if (status === 'CHANNEL_ERROR') {
          console.warn(`[Presence] WebSocket connection failed to ${actualChannelName}:`, err);
          setIsConnected(false);
          setOnlineUsers([]);
          // Removed manual retry; Supabase Realtime handles reconnections automatically.
        }
      });

    return () => {
      mounted = false;
      console.log(`[Presence] Leaving channel: ${actualChannelName}`);
      channel.unsubscribe();
      setIsConnected(false);
    };
  }, [user, actualChannelName, tenantId, channelName]);

  return { onlineUsers, isConnected };
}
